import { CommentOutlined, EyeOutlined, SyncOutlined, UserOutlined } from "@ant-design/icons";
import { ICellRendererParams, ValueFormatterParams } from "ag-grid-community";
import { Button, Card, Pagination } from "antd";
import React, { useCallback, useState } from "react";
import { match } from "ts-pattern";
import { GridType } from "../../../../../types/grid";
import Grid from "../../../../atoms/Grid";
import { convertToSentenceCase, formatDate } from "../../../../libs/utils";
import LoaderTsx from "../../../Loader.tsx";
import { ServiceRequest, useGetServiceRequests } from "../api";
import { OptionKeys } from "../search";
import { ServiceRequestGrid } from "../types";
import { FormType } from "../useServiceRequestModal";
import { getDaysSinceInProgress } from "./getDaysSinceInProgress";

const defaultPage = 1;
const defaultPageSize = 10;

type ServiceRequestsGridProps<TData = unknown> = {
  handleOpen: (type: FormType, row?: ServiceRequestGrid) => void;
  selectedSearchBy: OptionKeys;
  searchValue?: string;
};

export const ServiceRequestsGrid = (props: ServiceRequestsGridProps) => {
  const { handleOpen, selectedSearchBy, searchValue } = props;
  const [currentPage, setCurrentPage] = useState(defaultPage);

  const hasSearchParams = Boolean(selectedSearchBy && searchValue);

  const params = {
    page: currentPage,
    perPage: defaultPageSize,
    ...(hasSearchParams && { [selectedSearchBy]: searchValue }),
  };

  const { serviceRequests, isLoadingServiceRequests } = useGetServiceRequests({ params });

  const columns: GridType[] = [
    { field: "ticketId", hide: true },
    { field: "srNumber", headerName: "Sr no.", flex: 1, minWidth: 150 },
    { field: "category", headerName: "Category", flex: 1, minWidth: 210 },
    { field: "status", headerName: "Status", flex: 1, minWidth: 210 },
    { field: "serviceType", headerName: "Service Type", flex: 1, minWidth: 210 },
    { field: "assignedUser", headerName: "Assigned User", flex: 1, minWidth: 210 },
    { field: "openedFor", headerName: "Opened For", flex: 1, minWidth: 210 },
    { field: "createdAt", headerName: "Created Date", flex: 1, minWidth: 140 },
    { field: "actions", headerName: "Actions", flex: 0.4, minWidth: 170 },
  ];

  const gridData: ServiceRequestGrid[] = (serviceRequests?.data ?? []).map((item) => ({
    ticketId: item.ticketId,
    assignedUserId: item.landlord.landlordId,
    srNumber: item.srNumber,
    category: item.category.name,
    status: item.ticketStatus,
    serviceType: item.serviceType,
    assignedUser: item.landlord.email,
    createdAt: item.createdAt,
    openedFor: getDaysSinceInProgress(item?.ticketStatusHistory),
  }));

  const actionsCellRenderer = useCallback(
    (props: ICellRendererParams<ServiceRequestGrid>) => (
      <div>
        <Button type="text" size="small" onClick={() => handleOpen("changeStatus", props.data)}>
          <SyncOutlined />
        </Button>
        <Button type="text" size="small" onClick={() => handleOpen("assignUser", props.data)}>
          <UserOutlined />
        </Button>
        <Button type="text" size="small" onClick={() => handleOpen("addComment", props.data)}>
          <CommentOutlined />
        </Button>
        <Button type="text" size="small" onClick={() => handleOpen("detail", props.data)}>
          <EyeOutlined />
        </Button>
      </div>
    ),
    [handleOpen]
  );

  const formatGridColumns = (defaultColumns: GridType[]): GridType[] => {
    return defaultColumns.map((column) =>
      match(column.field)
        .with("status", () => ({
          ...column,
          valueFormatter: (params: ValueFormatterParams<ServiceRequest>) => convertToSentenceCase(params.value),
        }))
        .with("createdAt", () => ({
          ...column,
          valueFormatter: (params: ValueFormatterParams<ServiceRequest>) => formatDate(params.value),
        }))
        .with("actions", () => ({ ...column, cellRenderer: actionsCellRenderer }))
        .otherwise(() => column)
    );
  };

  return (
    <div className="managers_tenants_detail" style={{ marginTop: "1.5rem" }}>
      <Card bordered={false} className="ag-nospace-body">
        {isLoadingServiceRequests ? (
          <LoaderTsx />
        ) : (
          <>
            <Pagination
              className="ag-property-text-pagination"
              style={{ padding: "0.5rem" }}
              total={serviceRequests?.totalCount}
              showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} Service Requests`}
              defaultPageSize={defaultPageSize}
              current={currentPage}
              onChange={setCurrentPage}
            />
            <Grid
              className="p-0"
              height="calc(100vh - 22.25rem)"
              label="Service Requests"
              data={gridData}
              columns={formatGridColumns(columns)}
            />
          </>
        )}
      </Card>
    </div>
  );
};
